import { TableUI } from "components/design/TableUI";
import Async from "components/DesignSystem/AsyncComponents/Async";
import Slider from "components/DesignSystem/Slider/Slider";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { useGetAllShareholdersQuery } from "store/apis/Shareholders";
import { Shareholder } from "types/Models/Shareholders";
import { Person } from "types/Models/apAutofills";
import ShareholderAction from "./ShareholderAction";
import {
  useGetEntityAPDetailsQuery,
  useGetPeopleForEntityQuery,
  useUpdateEntityAPDetailsMutation,
} from "store/apis/directorsAndOfficers";
import { NoAuthorisedPersonaTypeExist } from "../NoAuthorisedPersonaTypeExist";
import { AddPersonModal } from "../People/AddPersonModal";
import { mergePersonAndShareholder } from "../People/personDetailsUtils";
import { PersonSlider } from "../People/PersonSlider";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import { ConfirmNoShareholderModal } from "./ConfirmNoShareholderModal";
import { EntityDetailsSlider } from "./EntityDetailsSlider";
import { ShareholderIcon } from "components/icons/ShareholderIcon";
import { EmptyStateWindIcon } from "components/icons/EmptyStateWindIcon";

export const ShareHolders = ({ onAddPerson }: { onAddPerson: () => void }) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const { entityId } = useParams<{ entityId: string }>();
  const {
    data: shareholders = [],
    isLoading,
    isSuccess,
  } = useGetAllShareholdersQuery(
    { entityId, groupId },
    { skip: !groupId || !entityId }
  );
  const { data: entityAPDetails } = useGetEntityAPDetailsQuery(
    { entityId },
    { skip: !entityId }
  );
  const { data: peopleData } = useGetPeopleForEntityQuery(
    { entityId },
    { skip: !entityId }
  );
  const [updateEntityAPDetails] = useUpdateEntityAPDetailsMutation();
  const { alertToast, successToast } = useToast();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [detailsShareholderId, setDetailsShareholderId] = useState<
    string | null
  >(null);
  const [editingShareholderId, setEditingShareholderId] = useState<
    string | null
  >(null);
  const detailsShareholder =
    shareholders.find((s) => s.uuid === detailsShareholderId) || null;
  const editingShareholder =
    shareholders.find((s) => s.uuid === editingShareholderId) || null;

  const matchedPersonFor = (shareholder: Shareholder): Person | undefined => {
    const personaAutofillId = shareholder.persona_details?.persona_autofill_id;
    if (!personaAutofillId) return undefined;
    const allPeople = [
      ...(peopleData?.people || []),
      ...(peopleData?.former_people || []),
    ];
    return allPeople.find(
      (person) => person.persona_autofill_id === personaAutofillId
    );
  };

  const buildPersonFromShareholder = (shareholder: Shareholder) =>
    mergePersonAndShareholder(matchedPersonFor(shareholder), shareholder);

  const createColumn = createColumnHelper<Shareholder>();

  const columns = [
    createColumn.display({
      id: "name",
      header: () => "Name",
      size: 26,
      cell: (info) => (
        <div className="t-flex t-flex-col">
          <div className="t-text-text-100">{info.row.original.name || "-"}</div>
          {info.row.original.persona_details?.email && (
            <div className="t-text-body-sm t-text-text-30">
              {info.row.original.persona_details.email}
            </div>
          )}
        </div>
      ),
    }),

    createColumn.display({
      id: "type",
      header: () => "Type",
      size: 18,
      cell: (info) => (
        <div className="t-text-text-100">
          {info.row.original.shareholder_type === "COMPANY_ENTITY"
            ? "Entity"
            : "Individual"}
        </div>
      ),
    }),

    createColumn.accessor("country.name", {
      cell: (info) => (
        <div className="t-text-text-60">{info.getValue() || "-"}</div>
      ),
      header: () => "Tax Residency",
      size: 27,
    }),

    createColumn.display({
      id: "ownership",
      header: () => "Ownership %",
      size: 20,
      cell: (info) => (
        <div className="t-text-text-60">
          {info.row.original.ownership_stake != null
            ? `${info.row.original.ownership_stake}%`
            : "-"}
        </div>
      ),
    }),

    createColumn.display({
      size: 5,
      header: " ",
      cell: (info) => (
        <ShareholderAction
          shareholder={info.row.original}
          onViewDetails={(shareholder) =>
            setDetailsShareholderId(shareholder.uuid)
          }
          onEditPersona={(shareholder) =>
            setEditingShareholderId(shareholder.uuid)
          }
        />
      ),
    }),
  ];

  const table = useReactTable({
    data: shareholders,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: { size: 10, minSize: 1, maxSize: 100 },
  });

  const handleConfirmPersonaCta = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmPersona = async () => {
    try {
      if (entityAPDetails) {
        await updateEntityAPDetails({
          payload: { ...entityAPDetails, shareholder_exists: false },
          entityId,
        }).unwrap();
        successToast({ message: `Shareholder status updated.` });
      } else {
        onAddPerson();
      }
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const isEmpty = !shareholders || shareholders.length === 0;

  return (
    <div className="t-flex t-flex-col t-rounded-xl t-border t-border-solid t-border-neutral-10 t-bg-surface t-p-5">
      <div className="t-mb-5 t-flex t-items-start t-justify-between">
        <div className="t-flex t-items-start t-gap-3">
          <span className="t-flex t-h-9 t-w-9 t-flex-shrink-0 t-items-center t-justify-center t-rounded t-bg-neutral-0 t-text-neutral-40">
            <ShareholderIcon />
          </span>
          <div>
            <div className="t-text-subtext t-text-text-100">Shareholders</div>
            <div className="t-text-body-sm t-text-text-30">
              Individuals and entities that own shares in the company.
            </div>
          </div>
        </div>
      </div>
      {entityAPDetails?.shareholder_exists === false ? (
        <NoAuthorisedPersonaTypeExist
          readOnly
          title="No shares issued"
          description={
            <>
              <div>You have confirmed that no shares have been issued yet.</div>
              <div>
                You can add a shareholder once the first issuance occurs.
              </div>
            </>
          }
          icon={<EmptyStateWindIcon />}
        />
      ) : (
        <Async.Root {...{ isLoading, isEmpty, isSuccess }}>
          <Async.Empty>
            <NoAuthorisedPersonaTypeExist
              ctaText="Add new"
              title="No shareholders added"
              description={
                <>
                  Add shareholders or confirm if there are none appointed yet.
                </>
              }
              handleAdd={onAddPerson}
              handleConfirm={handleConfirmPersonaCta}
              confirmCtaText="No shares issued yet"
              icon={<EmptyStateWindIcon />}
            />
          </Async.Empty>
          <Async.Success>
            <TableUI
              size="regular"
              table={table}
              onRowClick={(row) => setDetailsShareholderId(row.original.uuid)}
              isHeaderCellUpperCase={false}
              layout="fixed"
            />
          </Async.Success>
        </Async.Root>
      )}
      <ConfirmNoShareholderModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={() => {
          handleConfirmPersona();
          setShowConfirmModal(false);
        }}
      />
      <Slider.Root
        open={Boolean(detailsShareholder)}
        onOpenChange={(open) => !open && setDetailsShareholderId(null)}
      >
        <Slider.Content open={Boolean(detailsShareholder)}>
          {detailsShareholder &&
            (detailsShareholder.shareholder_type === "COMPANY_ENTITY" ? (
              <EntityDetailsSlider shareholder={detailsShareholder} />
            ) : (
              (() => {
                const person = buildPersonFromShareholder(detailsShareholder);
                return person ? (
                  <PersonSlider
                    person={person}
                    onEdit={() => {
                      setDetailsShareholderId(null);
                      setEditingShareholderId(detailsShareholder.uuid);
                    }}
                  />
                ) : null;
              })()
            ))}
        </Slider.Content>
      </Slider.Root>
      {editingShareholder &&
        (() => {
          const editPerson = buildPersonFromShareholder(editingShareholder);
          return (
            editPerson && (
              <AddPersonModal
                isOpen
                close={() => setEditingShareholderId(null)}
                personToEdit={editPerson}
                shareholderToEdit={editingShareholder}
              />
            )
          );
        })()}
    </div>
  );
};
